import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { NbCardModule, NbCheckboxModule, NbDialogRef, NbOptionModule, NbSelectModule } from '@nebular/theme';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subscription, combineLatest, of, switchMap } from 'rxjs';
import { KPI } from '../../../@core/api';
import { ChartFiltersService, InvestmentScenariiService, PerimeterEnergyTrajectoryService, PerimetersService, ReportsService, ScopeService } from '../../../@core/utils';
import { EmissionFactorService } from '../../../@core/utils/emission-factor.service';
import { YearSelectorComponent } from '../year-selector/year-selector.component';

@Component({
    selector: 'app-powerpoint-reporting-dialog',
    templateUrl: './powerpoint-reporting-dialog.component.html',
    styleUrls: ['./powerpoint-reporting-dialog.component.scss'],
    standalone: true,
    imports: [
      NbCardModule,
      NbSelectModule,
      FormsModule,
      NbOptionModule,
      NbCheckboxModule,
      YearSelectorComponent,
      FontAwesomeModule,
      TranslateModule,
      
    ]
})
export class PowerpointReportingDialogComponent implements OnInit, OnDestroy {

  public downloading: boolean = false;
  @Input() public inputData: KPI.ReportTemplateInputData;
  @Input() public template: KPI.ReportTemplate;
  public perimeterTypes: KPI.PerimeterType[] = [];
  public selectedPerimeterTypes: KPI.PerimeterType[] = [];
  public levelOneSubPerimeters: KPI.Perimeter[] = [];
  public selectedLevelOneSubPerimeters: KPI.Perimeter[] = [];
  public scenarii: KPI.InvestmentScenario[] = [];
  public scenario: KPI.InvestmentScenario | null;
  public includeHypothesis: Boolean = true;
  public includePlanned: Boolean = true;
  public startYear: number;
  public endYear: number;
  public scopeString: string;
  public disableDownload: boolean = false;
  private subscriptions: Subscription[] = [];
  private energyProvider: KPI.EmissionFactorProvider;
  private energyTypeOfUse: string;
  private carbonProvider: KPI.EmissionFactorProvider;
  private carbonTypeOfUse: string;
  private energyConsumptionType: KPI.ConsumptionType;
  private carbonConsumptionType: KPI.ConsumptionType;
  public loading: boolean;
  public isAllSelected = false;
  public readonly ALL_OPTION_VALUE = '__ALL__';
  public selectedPerimeterTypesDisplay: (KPI.PerimeterType | string)[] = [];
  private previousDisplaySelection: (KPI.PerimeterType | string)[] = [];
  public isAllPerimetersSelected = false;
  public selectedLevelOneSubPerimetersDisplay: (KPI.Perimeter | string)[] = [];
  private previousPerimeterDisplaySelection: (KPI.Perimeter | string)[] = [];

  public isSingleSite = false;
  public disabledPerimeterIds: Set<number> = new Set();
  // US 9310: default to "Whole site" (no building filter) unless a building subset
  // is chosen here or was carried over from the trajectory page.
  public isWholeSiteSelected = true;

  constructor(
    private ref: NbDialogRef<PowerpointReportingDialogComponent>,
    private reportsService: ReportsService,
    private investmentScenariiService: InvestmentScenariiService,
    private emissionFactorService: EmissionFactorService,
    private chartFiltersService: ChartFiltersService,
    private scopeService: ScopeService,
    private perimeterService: PerimetersService,
    private translate: TranslateService,
    private perimeterEnergyTrajectoryService: PerimeterEnergyTrajectoryService
  ) { }

  ngOnInit(): void {
    this.loading = true;
    this.startYear = this.inputData?.start_year || 2023;
    this.endYear = this.inputData?.end_year || 2030;
    if (this.template?.specific_data?.displayScenarioSelector) {
      this.subscriptions.push(
        this.investmentScenariiService.scenarii$.subscribe((enabledScenarii) => {
          this.scenarii = enabledScenarii.filter(s=>!s.is_main)
        }),
        this.investmentScenariiService.getSelectedScenario('right').subscribe((selectedScenario) => {
          if (selectedScenario && !selectedScenario.is_main) {
            this.scenario = selectedScenario;
          }
        }),
      )
    }
    this.subscriptions.push(
      combineLatest([
        this.emissionFactorService.getSelectedProvider$('energy'),
        this.emissionFactorService.getSelectedTypeOfUse$('energy'),
        this.emissionFactorService.getSelectedProvider$('carbon'),
        this.emissionFactorService.getSelectedTypeOfUse$('carbon'),
        this.chartFiltersService.filter$('energyConsumptionType', false),
        this.chartFiltersService.filter$('carbonConsumptionType', false),
        this.perimeterService.perimeters$,
        this.scopeService.perimeterTypes$,
      ]).pipe(
        switchMap(([
          energyProvider,
          energyTypeOfUse,
          carbonProvider,
          carbonTypeOfUse,
          energyConsumptionType,
          carbonConsumptionType,
          perimeters,
          perimeterTypes,
        ]) => {
          this.energyProvider = energyProvider;
          this.energyTypeOfUse = energyTypeOfUse;
          this.carbonProvider = carbonProvider;
          this.carbonTypeOfUse = carbonTypeOfUse;
          this.energyConsumptionType = <KPI.ConsumptionType>energyConsumptionType;
          this.carbonConsumptionType = <KPI.ConsumptionType>carbonConsumptionType;

          this.scopeString = this.translate.instant('Scope') + ' :';
          if (perimeters.length > 1) {
            this.scopeString += (' ' + this.translate.instant('X sites', { sitesNb: perimeters.length }));
          } else if (perimeters.length === 1) {
            this.scopeString += (' ' + perimeters[0].name);
          }

          const allLevelOne = perimeters.map(p => p.sub_perimeters).flat().filter(p => p.level_parent === null);
          this.levelOneSubPerimeters = this.template?.mnemonic === 'energy_report'
            ? allLevelOne.filter(p => perimeterTypes[p.perimeter_type]?.perimeter_type_class === KPI.PerimeterTypeClass.CLASS_BUILDING)
            : allLevelOne;

          this.disabledPerimeterIds = new Set();
          if (this.template?.mnemonic === 'energy_report') {
            this.levelOneSubPerimeters.forEach(p => {
              if (!p.building_id || !p.flooring_area) {
                this.disabledPerimeterIds.add(p.id);
              }
            });
          }

          this.isSingleSite = perimeters.length === 1;

          const enabledPerimeters = this.levelOneSubPerimeters.filter(p => !this.disabledPerimeterIds.has(p.id));

          if (this.inputData?.level_one_sub_perimeter_ids?.length) {
            this.selectedLevelOneSubPerimeters = enabledPerimeters.filter((p) =>
              this.inputData.level_one_sub_perimeter_ids.includes(p.id)
            );
            this.isWholeSiteSelected = false;
          } else if (this.isSingleSite) {
            // Pre-select from trajectory page's building selection
            const trajectoryBuildingIds = this.perimeterEnergyTrajectoryService.selectedTrajectoryBuildingIds;
            if (trajectoryBuildingIds.length > 0) {
              this.selectedLevelOneSubPerimeters = enabledPerimeters.filter((p) =>
                trajectoryBuildingIds.includes(p.building_id)
              );
              this.isWholeSiteSelected = false;
            } else {
              this.selectedLevelOneSubPerimeters = [...enabledPerimeters];
              this.isWholeSiteSelected = true;
            }
          } else {
            this.selectedLevelOneSubPerimeters = [...enabledPerimeters];
            this.isWholeSiteSelected = true;
          }
          const hasDisabledPerimeters = this.filteredLevelOneSubPerimeters().some(p => this.disabledPerimeterIds.has(p.id));
          this.isAllPerimetersSelected = !hasDisabledPerimeters && this.selectedLevelOneSubPerimeters.length === enabledPerimeters.length && enabledPerimeters.length > 0;
          this.updatePerimeterSelectedDisplay();

          return perimeters.length === 1
            ? this.perimeterEnergyTrajectoryService.getPerimeterEnergyTrajectories(perimeters[0].id)
            : of([]);
        })
      ).subscribe((energyTrajectories) => {
        if (energyTrajectories.length === 0 && this.template.mnemonic === "energy_report") {
          this.disableDownload = true;
        }
        this.loading = false;
      })
    );
    this.subscriptions.push(
      this.perimeterService.perimeters$.pipe(
        switchMap((perimeters) =>
          this.scopeService.getCustomerPerimeterTypes(perimeters)
        )
      ).subscribe((perimeterTypes) => {
        this.perimeterTypes = perimeterTypes.filter((pT) => pT.level === 1);
        if (this.inputData?.perimeter_type_ids?.length) {
          this.selectedPerimeterTypes = this.perimeterTypes.filter((pt) =>
            this.inputData.perimeter_type_ids.includes(pt.id)
          );
        } else {
          this.selectedPerimeterTypes = [...this.perimeterTypes];
        }
        this.isAllSelected = true;
        this.updateSelectedDisplay();
        this.resetInvalidSubPerimeter();
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
  }

  public checkYears() {
    if (this.endYear < this.startYear) {
      const temp = this.endYear;
      this.endYear = this.startYear;
      this.startYear = temp;
    }
  }

  public startYearChanged(startYear: number): void {
    this.startYear = startYear;
    this.checkYears();
  }

  public endYearChanged(endYear: number): void {
    this.endYear = endYear;
    this.checkYears();
  }

  public onDisplaySelectionChanged(selected: any[]): void {
    const toggledAll = this.hasAllToggled(this.previousDisplaySelection, selected);

    if (toggledAll) {
      if (this.isAllSelected) {
        this.selectedPerimeterTypes = [];
        this.isAllSelected = false;
      } else {
        this.selectedPerimeterTypes = [...this.perimeterTypes];
        this.isAllSelected = true;
      }
    } else {
      this.selectedPerimeterTypes = selected.filter(
        (p: any): p is KPI.PerimeterType =>
          p && typeof p === 'object' && 'id' in p
      );

      this.isAllSelected =
        this.selectedPerimeterTypes.length === this.perimeterTypes.length &&
        this.perimeterTypes.every((pt) =>
          this.selectedPerimeterTypes.some((sel) => sel.id === pt.id)
        );
    }

    this.updateSelectedDisplay();
    this.resetInvalidSubPerimeter();
  }

  private updateSelectedDisplay(): void {
    this.selectedPerimeterTypesDisplay = this.isAllSelected
      ? [this.ALL_OPTION_VALUE, ...this.selectedPerimeterTypes]
      : [...this.selectedPerimeterTypes];

    this.previousDisplaySelection = [...this.selectedPerimeterTypesDisplay];
  }

  private hasAllToggled(previous: any[], current: any[]): boolean {
    const hadAll = previous.includes(this.ALL_OPTION_VALUE);
    const hasAll = current.includes(this.ALL_OPTION_VALUE);
    return hadAll !== hasAll;
  }

  private resetInvalidSubPerimeter(): void {
    const enabledPerimeters = this.enabledFilteredPerimeters();
    this.selectedLevelOneSubPerimeters = this.selectedLevelOneSubPerimeters.filter(selected =>
      enabledPerimeters.some(filtered => filtered.id === selected.id)
    );

    const hasDisabledPerimeters = this.filteredLevelOneSubPerimeters().some(p => this.disabledPerimeterIds.has(p.id));
    this.isAllPerimetersSelected =
      !hasDisabledPerimeters &&
      this.selectedLevelOneSubPerimeters.length === enabledPerimeters.length &&
      enabledPerimeters.length > 0 &&
      enabledPerimeters.every((p) =>
        this.selectedLevelOneSubPerimeters.some((sel) => sel.id === p.id)
      );

    this.updatePerimeterSelectedDisplay();
  }

  public filteredLevelOneSubPerimeters(): KPI.Perimeter[] {
    return this.levelOneSubPerimeters.filter(p =>
      this.selectedPerimeterTypes.length === 0
      || this.selectedPerimeterTypes.some((pt) => pt.id === p.perimeter_type)
    );
  }

  public isPerimeterDisabled(perimeter: KPI.Perimeter): boolean {
    return this.disabledPerimeterIds.has(perimeter.id);
  }

  public enabledFilteredPerimeters(): KPI.Perimeter[] {
    return this.filteredLevelOneSubPerimeters().filter(p => !this.disabledPerimeterIds.has(p.id));
  }

  public canDownload(): boolean {
    return true; // in case some inputs are required
  }

  public onPerimeterDisplaySelectionChanged(selected: any[]): void {
    const toggledAll = this.hasAllToggledPerimeter(this.previousPerimeterDisplaySelection, selected);

    let newSelected: KPI.Perimeter[];
    const enabledPerimeters = this.enabledFilteredPerimeters();

    if (toggledAll) {
      // user clicked the "Whole site" row
      this.isWholeSiteSelected = !this.isWholeSiteSelected;
      newSelected = this.isWholeSiteSelected
        ? [...enabledPerimeters]
        : [...this.selectedLevelOneSubPerimeters];
    } else {
      this.isWholeSiteSelected = false;
      newSelected = selected.filter(
        (p: any): p is KPI.Perimeter =>
          p && typeof p === 'object' && 'id' in p
      );
    }

    if (newSelected.length === 0) {
      this.updatePerimeterSelectedDisplay();
      return;
    }

    this.selectedLevelOneSubPerimeters = newSelected;

    const hasDisabledPerimeters = this.filteredLevelOneSubPerimeters().some(p => this.disabledPerimeterIds.has(p.id));
    this.isAllPerimetersSelected =
      !hasDisabledPerimeters &&
      this.selectedLevelOneSubPerimeters.length === enabledPerimeters.length &&
      enabledPerimeters.length > 0 &&
      enabledPerimeters.every((p) =>
        this.selectedLevelOneSubPerimeters.some((sel) => sel.id === p.id)
      );

    this.updatePerimeterSelectedDisplay();
  }

  private updatePerimeterSelectedDisplay(): void {
    this.selectedLevelOneSubPerimetersDisplay = (this.isWholeSiteSelected || this.isAllPerimetersSelected)
      ? [this.ALL_OPTION_VALUE, ...this.selectedLevelOneSubPerimeters]
      : [...this.selectedLevelOneSubPerimeters];

    this.previousPerimeterDisplaySelection = [...this.selectedLevelOneSubPerimetersDisplay];
  }

  private hasAllToggledPerimeter(previous: any[], current: any[]): boolean {
    const hadAll = previous.includes(this.ALL_OPTION_VALUE);
    const hasAll = current.includes(this.ALL_OPTION_VALUE);
    return hadAll !== hasAll;
  }

  public download(): void {
    this.reportsService.isAnyReportDownloading$.next(true);
    this.downloading = true;

    if (this.template && this.inputData) {
      if (this.template?.specific_data?.displayScenarioSelector) {
        this.inputData!.decarbonized_scenario = this.scenario?.id;
      }
      if (this.template?.specific_data?.displayPerimeterSelectors && !this.isWholeSiteSelected) {
        const selectedIds = this.selectedLevelOneSubPerimeters.map(p => p.id);
        if (selectedIds.length > 0) {
          this.inputData!.level_one_sub_perimeter_ids = selectedIds;
        }
      }

      this.inputData!.perimeter_type_ids = this.selectedPerimeterTypes.map(pt => pt.id);
      this.inputData!.include_hypothesis = this.includeHypothesis;
      this.inputData!.include_planned = this.includePlanned;
      this.inputData!.energy_provider = this.energyProvider?.id;
      this.inputData!.energy_type_of_use = this.energyTypeOfUse;
      this.inputData!.carbon_provider = this.carbonProvider?.id;
      this.inputData!.carbon_type_of_use = this.carbonTypeOfUse;
      this.inputData!.start_year = this.startYear;
      this.inputData!.end_year = this.endYear;
      this.inputData!.energy_consumption_key = this.energyConsumptionType;
      this.inputData!.carbon_consumption_key = this.carbonConsumptionType;

      this.reportsService.downloadReport(this.inputData, this.template.id).subscribe(()=>{
          this.downloading = false;
          this.reportsService.isAnyReportDownloading$.next(false);
          this.ref.close();
        }
      );
    }
  }

  public close(): void {
    this.ref.close();
  }
}
